import { Confirm, Loading, Notify } from 'notiflix';

import { reveal, topLayerHost } from './topLayerHost.js';

Notify.init({
  position: 'right-top',
  clickToClose: true
});

// The ids Notiflix gives its containers. It offers no option for where they
// are mounted - every component calls document.body.appendChild directly - so
// they are found by id after the fact and moved into the top-layer host. See
// topLayerHost.js for why they have to be there at all.
const CONTAINER_IDS = [
  'NotiflixNotifyWrap',
  'NotiflixNotifyOverlay',
  'NotiflixLoadingWrap',
  'NotiflixConfirmWrap'
];

function containers() {
  return CONTAINER_IDS.map((id) => document.getElementById(id));
}

// Notiflix paints synchronously, so by the time one of its calls returns, its
// container is in the document and can be adopted. Notify is the exception
// that needs no more than this: it looks its wrap up by id and reuses it
// wherever it sits, so once moved it stays moved. Loading and Confirm build a
// fresh element per call and append it to <body> again, which is why every
// call goes through here rather than only the first.
function afterNotiflix() {
  reveal(...containers());
}

// Notiflix paints its dialog with inline styles, so it cannot follow the active
// daisyUI theme on its own. The theme can also change without a page load (the
// .theme-controller radios on the settings page), so the colours are read at
// call time rather than captured once when this module loads.
function themeColor(name, fallback) {
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name);
  return value.trim() || fallback;
}

// daisyUI 5 states its palette in oklch, which goes straight into Notiflix'
// style attributes - no conversion needed, since any browser that renders this
// app at all already parses oklch.
function confirmColors() {
  return {
    backgroundColor: themeColor('--color-base-100', '#ffffff'),
    titleColor: themeColor('--color-primary', '#570df8'),
    messageColor: themeColor('--color-base-content', '#1e1e1e'),
    okButtonBackground: themeColor('--color-primary', '#570df8'),
    okButtonColor: themeColor('--color-primary-content', '#ffffff'),
    cancelButtonBackground: themeColor('--color-base-300', '#a9a9a9'),
    cancelButtonColor: themeColor('--color-base-content', '#1e1e1e')
  };
}

export function notifySuccess(message) {
  Notify.success(message);
  afterNotiflix();
}

export function notifyError(message) {
  Notify.failure(message);
  afterNotiflix();
}

export function notifyInfo(message) {
  Notify.info(message);
  afterNotiflix();
}

export function notifyWarning(message) {
  Notify.warning(message);
  afterNotiflix();
}

// Blocks the screen while a request is running, so nobody clicks twice.
export function showLoading(message) {
  Loading.pulse(message);
  afterNotiflix();
}

export function hideLoading() {
  Loading.remove();
}

// Wraps Notiflix' callback-based dialog in a promise: `if (await confirmAction(…))`.
export function confirmAction({ title, message, okText = 'Ja', cancelText = 'Abbrechen' }) {
  return new Promise((resolve) => {
    Confirm.show(
      title,
      message,
      okText,
      cancelText,
      () => resolve(true),
      () => resolve(false),
      confirmColors()
    );
    afterNotiflix();
  });
}

// Exported for the one case the host cannot infer: a dialog opened while a
// Loading overlay is already up would land above it. formModal.js calls this
// after showModal() so the overlay is promoted again on top.
export function raiseOverlays() {
  if (topLayerHost()) afterNotiflix();
}
